import { roundPercent } from '@nt/onboarding-rules'
import { cn } from '@nt/ui/cn'
import * as React from 'react'

import { Input } from '@/components/ui/input'

interface PercentageInputProps {
  value: number
  onChange: (value: number) => void
  onBlur?: () => void
  max?: number
  className?: string
  'aria-label'?: string
}

const inputStyles =
  'appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'

export function PercentageInput({
  value,
  onChange,
  onBlur,
  max = 100,
  className,
  'aria-label': ariaLabel,
}: PercentageInputProps) {
  const [localValue, setLocalValue] = React.useState(String(value))
  const [isFocused, setIsFocused] = React.useState(false)

  // Sync from external value when not focused — done during render (the
  // "adjust state during render" pattern) rather than in a useEffect, by
  // comparing against the last-seen `value`/`isFocused` pair. This covers
  // both an external `value` change and a blur (isFocused flipping to
  // false), exactly like the effect it replaces.
  const [prevValue, setPrevValue] = React.useState(value)
  const [prevIsFocused, setPrevIsFocused] = React.useState(isFocused)
  if (value !== prevValue || isFocused !== prevIsFocused) {
    setPrevValue(value)
    setPrevIsFocused(isFocused)
    if (!isFocused) {
      setLocalValue(String(value))
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value

    // Allow comma as decimal separator, convert to dot
    inputValue = inputValue.replace(',', '.')

    // Any number of decimals may be TYPED (B14: refusing the third keystroke
    // read as truncation); the value handed up is rounded to 2 decimals.
    if (!/^\d*\.?\d*$/.test(inputValue)) return

    setLocalValue(inputValue)

    if (inputValue !== '' && inputValue !== '.' && !inputValue.endsWith('.')) {
      const numValue = parseFloat(inputValue)
      if (!isNaN(numValue) && numValue <= max) {
        onChange(roundPercent(numValue))
      }
    } else if (inputValue === '' || inputValue === '.') {
      onChange(0)
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)

    // On blur, ensure we have a valid value
    const numValue = parseFloat(localValue)
    if (isNaN(numValue) || localValue === '' || localValue === '.') {
      setLocalValue('0')
      onChange(0)
    } else {
      // Typing is capped at `max` (B14 in handleChange), but that only gates
      // the onChange call, not `localValue` itself — a value like "150" past
      // a 100 cap, or "60" past a 50 reserved-equity cap, could still reach
      // this branch on blur and get stored uncapped.
      const rounded = Math.min(roundPercent(numValue), max)
      setLocalValue(String(rounded))
      onChange(rounded)
    }

    onBlur?.()
  }

  return (
    <Input
      type="text"
      inputMode="decimal"
      aria-label={ariaLabel}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={cn('h-control w-20 text-right text-sm hover:border-primary', inputStyles, className)}
    />
  )
}
